const SYNC_PREFIX = 'bioa-';
const SKIP_KEYS = new Set(['bioa-session', 'bioa-api-base', 'bioa-master-session', 'bioa-force-desktop']);

type BioaSession = {
  token: string;
  userId: string;
  name: string;
  role: string;
  hospitalId: string;
  hospitalName: string;
  loginAt: string;
};

type MasterSession = {
  token: string;
  userId: string;
  role: string;
  loginAt: string;
};

export type HospitalRequest = {
  id: number;
  hospitalName: string;
  masterUserId: string;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  approvedHospitalId?: string | null;
  rejectReason?: string | null;
};

declare global {
  interface Window {
    __bioaServerSyncInstalled?: boolean;
    __bioaServerSyncApplying?: boolean;
  }
}

function apiBase() {
  const saved = localStorage.getItem('bioa-api-base');
  const hostname = window.location.hostname;
  const isLocalPage = !hostname || hostname === 'localhost' || hostname === '127.0.0.1';
  if (saved) return saved;
  return isLocalPage ? 'http://127.0.0.1:8765' : '';
}

function session(): BioaSession | null {
  try {
    const raw = localStorage.getItem('bioa-session');
    return raw ? JSON.parse(raw) as BioaSession : null;
  } catch {
    return null;
  }
}

function masterSession(): MasterSession | null {
  try {
    const raw = localStorage.getItem('bioa-master-session');
    return raw ? JSON.parse(raw) as MasterSession : null;
  } catch {
    return null;
  }
}

function authHeaders(): Record<string, string> {
  const current = session();
  return current?.token ? { Authorization: `Bearer ${current.token}` } : {};
}

function masterAuthHeaders(): Record<string, string> {
  const current = masterSession();
  return current?.token ? { Authorization: `Bearer ${current.token}` } : {};
}

function shouldSync(key: string) {
  return key.startsWith(SYNC_PREFIX) && !SKIP_KEYS.has(key);
}

function parseValue(value: string | null) {
  if (value === null) return null;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

async function sendValue(key: string, value: unknown) {
  if (!shouldSync(key)) return;
  if (!session()?.token) return;
  if (window.__bioaServerSyncApplying) return;
  const response = await fetch(`${apiBase()}/api/storage/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ value }),
  });
  if (!response.ok) {
    throw new Error(`BIOA server save failed: ${key}`);
  }
}

async function deleteValue(key: string) {
  if (!shouldSync(key)) return;
  if (!session()?.token) return;
  if (window.__bioaServerSyncApplying) return;
  const response = await fetch(`${apiBase()}/api/storage/${encodeURIComponent(key)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!response.ok) {
    throw new Error(`BIOA server delete failed: ${key}`);
  }
}

function installStoragePatch() {
  if (window.__bioaServerSyncInstalled) return;
  window.__bioaServerSyncInstalled = true;

  const originalSetItem = Storage.prototype.setItem;
  const originalRemoveItem = Storage.prototype.removeItem;

  Storage.prototype.setItem = function setItem(key: string, value: string) {
    originalSetItem.call(this, key, value);
    if (this === window.localStorage) {
      void sendValue(key, parseValue(value)).catch((error) => {
        window.dispatchEvent(new CustomEvent('bioa-server-error', { detail: error }));
      });
    }
  };

  Storage.prototype.removeItem = function removeItem(key: string) {
    originalRemoveItem.call(this, key);
    if (this === window.localStorage) {
      void deleteValue(key).catch((error) => {
        window.dispatchEvent(new CustomEvent('bioa-server-error', { detail: error }));
      });
    }
  };
}

export async function pullBootstrap() {
  if (!session()?.token) return;
  const response = await fetch(`${apiBase()}/api/bootstrap`, { headers: authHeaders() });
  if (response.status === 401) {
    localStorage.removeItem('bioa-session');
    return;
  }
  if (!response.ok) throw new Error(`BIOA server connection failed: ${response.status}`);
  const payload = await response.json() as { items?: Record<string, unknown> };
  const items = payload.items || {};

  window.__bioaServerSyncApplying = true;
  try {
    Object.keys(localStorage).forEach((key) => {
      if (shouldSync(key) && !(key in items)) {
        localStorage.removeItem(key);
      }
    });
    Object.entries(items).forEach(([key, value]) => {
      if (!shouldSync(key)) return;
      localStorage.setItem(key, JSON.stringify(value));
    });
  } finally {
    window.__bioaServerSyncApplying = false;
  }
}

export async function login(userId: string, password: string) {
  const response = await fetch(`${apiBase()}/api/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, password }),
  });
  if (!response.ok) {
    throw new Error('아이디 또는 비밀번호를 확인하세요.');
  }
  const payload = await response.json() as { session: BioaSession };
  localStorage.setItem('bioa-session', JSON.stringify(payload.session));
  await pullBootstrap();
  return payload.session;
}

export async function requestHospital(payload: { hospitalName: string; masterUserId: string; password: string }) {
  const response = await fetch(`${apiBase()}/api/hospital-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const result = await response.json().catch(() => ({})) as { error?: string; request?: HospitalRequest };
  if (!response.ok) {
    throw new Error(result.error || '병원 개설 요청에 실패했습니다.');
  }
  return result.request;
}

export async function masterLogin(userId: string, password: string) {
  const response = await fetch(`${apiBase()}/api/master/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, password }),
  });
  const result = await response.json().catch(() => ({})) as { error?: string; session?: MasterSession };
  if (!response.ok || !result.session) {
    throw new Error(result.error || '운영관리 로그인에 실패했습니다.');
  }
  localStorage.setItem('bioa-master-session', JSON.stringify(result.session));
  return result.session;
}

export async function listHospitalRequests() {
  const response = await fetch(`${apiBase()}/api/master/requests`, { headers: masterAuthHeaders() });
  const result = await response.json().catch(() => ({})) as { error?: string; requests?: HospitalRequest[] };
  if (!response.ok) {
    if (response.status === 401) localStorage.removeItem('bioa-master-session');
    throw new Error(result.error || '요청 목록을 불러오지 못했습니다.');
  }
  return result.requests || [];
}

export async function approveHospitalRequest(id: number) {
  const response = await fetch(`${apiBase()}/api/master/requests/${id}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...masterAuthHeaders() },
    body: JSON.stringify({}),
  });
  const result = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok) {
    throw new Error(result.error || '승인 처리에 실패했습니다.');
  }
  return result;
}

export async function rejectHospitalRequest(id: number, reason = '') {
  const response = await fetch(`${apiBase()}/api/master/requests/${id}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...masterAuthHeaders() },
    body: JSON.stringify({ reason }),
  });
  const result = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok) {
    throw new Error(result.error || '거절 처리에 실패했습니다.');
  }
  return result;
}

export async function installServerSync() {
  installStoragePatch();
  await pullBootstrap();
}

export type BioaFileRef = {
  id: string;
  name: string;
  mime: string;
  size: number;
  url: string;
};

export async function uploadFileDataUrl(payload: { name: string; mime: string; dataUrl: string }) {
  const response = await fetch(`${apiBase()}/api/files`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ name: payload.name, mime: payload.mime, dataUrl: payload.dataUrl }),
  });
  const result = await response.json().catch(() => ({})) as { error?: string; file?: BioaFileRef };
  if (!response.ok || !result.file) throw new Error(result.error || 'BIOA file upload failed');
  return result.file;
}

export async function fetchFileBlob(fileId: string) {
  const response = await fetch(`${apiBase()}/api/files/${encodeURIComponent(fileId)}`, { headers: authHeaders() });
  if (!response.ok) throw new Error(`BIOA file download failed: ${fileId}`);
  return response.blob();
}

export async function fetchFileArrayBuffer(fileId: string) {
  const response = await fetch(`${apiBase()}/api/files/${encodeURIComponent(fileId)}`, { headers: authHeaders() });
  if (!response.ok) throw new Error(`BIOA file download failed: ${fileId}`);
  return response.arrayBuffer();
}

export { apiBase };
